import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Upload, 
  FileSpreadsheet, 
  ChevronRight, 
  ChevronLeft,
  Check,
  X,
  AlertTriangle,
  AlertCircle,
  Users,
  RefreshCw,
  Loader2
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

// System fields for mapping
const SYSTEM_FIELDS = [
  { key: "email", label: "Email", required: true, description: "Contact email address" },
  { key: "first_name", label: "First Name", required: false, description: "Contact first name" },
  { key: "last_name", label: "Last Name", required: false, description: "Contact last name" },
  { key: "full_name", label: "Full Name", required: false, description: "Full name (overrides first+last)" },
  { key: "phone", label: "Phone", required: false, description: "Phone number" },
  { key: "company", label: "Company", required: false, description: "Company or organization" },
  { key: "tags", label: "Tags", required: false, description: "Comma-separated tags" },
  { key: "notes", label: "Notes", required: false, description: "Additional notes" },
];

export default function ContactImportWizard({ open, onClose, token, onImportComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [mapping, setMapping] = useState({});
  const [suggestedMapping, setSuggestedMapping] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const resetWizard = () => {
    setStep(1);
    setFile(null);
    setCsvHeaders([]);
    setRowCount(0);
    setMapping({});
    setSuggestedMapping({});
    setPreviewData(null);
    setImportResult(null);
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  // Step 1: File Upload
  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error("Only CSV files are supported");
      return;
    }

    // Validate file size (5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }

    setFile(selectedFile);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/contacts/import/headers`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setCsvHeaders(response.data.headers);
      setRowCount(response.data.row_count);
      setSuggestedMapping(response.data.suggested_mapping || {});
      setMapping(response.data.suggested_mapping || {});
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse CSV");
      setFile(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Step 2: Mapping
  const handleMappingChange = (systemField, csvColumn) => {
    setMapping(prev => ({
      ...prev,
      [systemField]: csvColumn === "none" ? null : csvColumn
    }));
  };

  const isEmailMapped = mapping.email && mapping.email !== "none";

  // Step 3: Preview
  const handlePreview = async () => {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));

      const response = await axios.post(`${API}/contacts/import/preview`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setPreviewData(response.data);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Commit Import
  const handleCommit = async () => {
    if (!previewData?.preview_token) return;

    setLoading(true);

    try {
      const response = await axios.post(`${API}/contacts/import/commit`, {
        preview_token: previewData.preview_token
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setImportResult(response.data);
      setStep(4);
      toast.success(`Import complete! Created ${response.data.created}, Updated ${response.data.updated}`);
      onImportComplete?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            Import Contacts from CSV
          </DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            {[
              { num: 1, label: "Upload" },
              { num: 2, label: "Map Fields" },
              { num: 3, label: "Preview" },
              { num: 4, label: "Done" }
            ].map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step >= s.num 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-100 text-gray-400'}`}
                >
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                {idx < 3 && (
                  <div className={`w-16 h-0.5 mx-2 ${step > s.num ? 'bg-primary' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Upload</span>
            <span>Map Fields</span>
            <span>Preview</span>
            <span>Done</span>
          </div>
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
            >
              {loading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  <p className="text-muted-foreground">Parsing CSV...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium">Drop your CSV file here</p>
                  <p className="text-sm text-muted-foreground mt-1">or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-3">
                    Max 5MB • Max 5,000 rows • UTF-8 only
                  </p>
                </>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />

            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="font-medium mb-2">Tips for best results:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Include an email column (required)</li>
                <li>Common headers like "First Name", "fname", or "given_name" are auto-detected</li>
                <li>Tags can be comma-separated in a single column</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Mapping */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{file?.name}</span>
              </div>
              <Badge variant="outline">{rowCount} rows</Badge>
            </div>

            <p className="text-sm text-muted-foreground">
              Map your CSV columns to contact fields. Email is required.
            </p>

            <div className="space-y-3 max-h-80 overflow-y-auto">
              {SYSTEM_FIELDS.map((field) => (
                <div key={field.key} className="flex items-center gap-3">
                  <div className="w-32 flex-shrink-0">
                    <p className="text-sm font-medium">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{field.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <Select
                    value={mapping[field.key] || "none"}
                    onValueChange={(val) => handleMappingChange(field.key, val)}
                  >
                    <SelectTrigger className={`flex-1 ${
                      field.required && !mapping[field.key] ? 'border-red-300' : ''
                    } ${
                      suggestedMapping[field.key] === mapping[field.key] && mapping[field.key] 
                        ? 'border-green-300 bg-green-50' 
                        : ''
                    }`}>
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Not mapped --</SelectItem>
                      {csvHeaders.map((header) => (
                        <SelectItem key={header} value={header}>
                          {header}
                          {suggestedMapping[field.key] === header && (
                            <span className="text-green-600 ml-2">(suggested)</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {!isEmailMapped && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                Email column must be mapped to continue
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button 
                onClick={handlePreview} 
                disabled={!isEmailMapped || loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <ChevronRight className="w-4 h-4 mr-1" />
                )}
                Preview Import
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && previewData && (
          <div className="space-y-4">
            {/* High Update Warning */}
            {previewData.high_update_ratio_warning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">High Update Ratio Warning</p>
                  <p className="text-sm text-amber-700">
                    This import will update more than 80% of your existing contacts. 
                    Please verify this is the correct file before proceeding.
                  </p>
                </div>
              </div>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{previewData.will_create}</p>
                <p className="text-xs text-green-600">Will Create</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{previewData.will_update}</p>
                <p className="text-xs text-blue-600">Will Update</p>
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-gray-700">{previewData.will_skip}</p>
                <p className="text-xs text-gray-600">Will Skip</p>
              </div>
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-purple-700">{previewData.duplicate_rows_merged}</p>
                <p className="text-xs text-purple-600">Duplicates Merged</p>
              </div>
            </div>

            {/* Validation Details */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total rows in file:</span>
                <span className="font-medium">{previewData.total_rows}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valid rows:</span>
                <span className="font-medium text-green-600">{previewData.valid_rows}</span>
              </div>
              {previewData.rows_missing_email > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>Missing email:</span>
                  <span>{previewData.rows_missing_email}</span>
                </div>
              )}
              {previewData.rows_invalid_email > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Invalid email:</span>
                  <span>{previewData.rows_invalid_email}</span>
                </div>
              )}
            </div>

            {/* Sample Errors */}
            {previewData.sample_errors?.length > 0 && (
              <div className="border border-red-100 rounded-lg p-3">
                <p className="font-medium text-red-700 mb-2 flex items-center gap-2">
                  <X className="w-4 h-4" />
                  Sample Errors
                </p>
                <div className="space-y-1 text-sm max-h-32 overflow-y-auto">
                  {previewData.sample_errors.map((err, i) => (
                    <div key={i} className="text-red-600">
                      Row {err.row}: {err.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sample Preview */}
            {previewData.sample_contacts?.length > 0 && (
              <div className="border rounded-lg p-3">
                <p className="font-medium mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Sample Contacts (first {Math.min(previewData.sample_contacts.length, 5)})
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2 px-2 font-medium text-muted-foreground">Email</th>
                        <th className="py-2 px-2 font-medium text-muted-foreground">First Name</th>
                        <th className="py-2 px-2 font-medium text-muted-foreground">Last Name</th>
                        <th className="py-2 px-2 font-medium text-muted-foreground">Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.sample_contacts.slice(0, 5).map((contact, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 px-2 truncate max-w-[200px]">{contact.email || "—"}</td>
                          <td className="py-2 px-2">{contact.first_name || <span className="text-muted-foreground italic">—</span>}</td>
                          <td className="py-2 px-2">{contact.last_name || <span className="text-muted-foreground italic">—</span>}</td>
                          <td className="py-2 px-2 truncate max-w-[150px]">{contact.company || <span className="text-muted-foreground">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Mapping
              </Button>
              <Button 
                onClick={handleCommit} 
                disabled={loading || previewData.valid_rows === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-1" />
                )}
                Confirm Import
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Done */}
        {step === 4 && importResult && (
          <div className="space-y-4 text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            
            <div>
              <h3 className="text-xl font-bold">Import Complete!</h3>
              <p className="text-muted-foreground">Your contacts have been imported successfully.</p>
            </div>

            <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-green-700">{importResult.created}</p>
                <p className="text-xs text-green-600">Created</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-blue-700">{importResult.updated}</p>
                <p className="text-xs text-blue-600">Updated</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-700">{importResult.skipped}</p>
                <p className="text-xs text-gray-600">Skipped</p>
              </div>
            </div>

            {importResult.errors?.length > 0 && (
              <div className="text-sm text-amber-600">
                {importResult.errors.length} row(s) had errors during import
              </div>
            )}

            <div className="pt-4">
              <Button onClick={handleClose} className="px-8">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
